/**
 * @file Pagination 分页
 * @description 页码切换 + 总数信息；紧凑。
 *
 * @whenToUse 列表底部分页。
 * @whenNotToUse 无限滚动场景。
 */

import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button.tsx'
import { cn } from '@/lib/cn.ts'

export type PaginationProps = {
  page: number
  pageSize: number
  total: number
  onChange: (page: number) => void
  className?: string
}

export function Pagination({
  page,
  pageSize,
  total,
  onChange,
  className,
}: PaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const safePage = Math.min(Math.max(1, page), pageCount)

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-2 text-sm',
        className,
      )}
    >
      <span className="text-muted">
        共 {total} 条
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          className="size-7 px-0"
          disabled={safePage <= 1}
          icon={<ChevronLeft className="size-4" aria-hidden />}
          aria-label="上一页"
          onClick={() => onChange(safePage - 1)}
        />
        <span className="text-muted">第 {safePage}/{pageCount} 页</span>
        <Button
          variant="secondary"
          className="size-7 px-0"
          disabled={safePage >= pageCount}
          icon={<ChevronRight className="size-4" aria-hidden />}
          aria-label="下一页"
          onClick={() => onChange(safePage + 1)}
        />
      </div>
    </div>
  )
}
